/**
 * Окно «звёзды пришли» после досмотренного ролика. Перенос
 * `app/src/components/RewardModal.tsx`; числа и правила те же.
 *
 * Раньше о награде сообщала строка под кнопкой. Её не замечали, и это понятно:
 * человек только что смотрел видео во весь экран, вернулся — а на прежнем месте
 * мелкая надпись, которую взгляд не ищет. Ради звёзд ролик и смотрят, значит и
 * сказать о них надо так, чтобы пропустить было нельзя.
 *
 * Показывается ТОЛЬКО когда звёзды действительно начислены. У суточного потолка
 * последний ролик может не дать ничего — поздравлять там не с чем, и для этого
 * случая осталась прежняя спокойная строка.
 */
import { createPortal } from 'react-dom'

export function RewardModal({
  stars,
  visible,
  title,
  body,
  ok,
  onClose,
}: {
  /** Сколько звёзд начислено. Ноль и меньше — окна нет. */
  /** Сколько звёзд начислено. Ноль — числа в окне нет вовсе. */
  stars: number
  /** Показывать ли окно. По умолчанию — когда есть что прибавить. */
  visible?: boolean
  title: string
  body: string
  ok: string
  onClose: () => void
}) {
  // Ноль перестал означать «окна нет» с тех пор, как за вход дают первое
  // гадание, а не звёзды: подтвердить подарок надо, а «+0» рисовать нельзя.
  if (!(visible ?? stars > 0)) return null

  /*
    Рисуем порталом в <body>, а не на месте, — по той же причине, что и крупный
    просмотр карты (`CardZoom`).

    Экран появляется с анимацией `fu`, и `fill-mode: both` оставляет на `.screen`
    `transform` навсегда. Элемент с `transform` становится системой отсчёта для
    `position: fixed` И своим слоем: наш `z-index: 60` замыкается внутри
    `.app__body` (слой 2), а нижняя панель с главной кнопкой — слой 3. То есть
    окно оказывалось ПОД кнопкой, подложка её не затемняла, и нажатие в то место,
    где нарисовано «Позже», попадало по «Сделать расклад». Замерено в браузере на
    боевом: `elementFromPoint` в центре «Позже» отдавал главную кнопку.
  */
  return createPortal(
    // Нажатие мимо окна закрывает: это поздравление, а не вопрос.
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28,
        background: 'rgba(9, 5, 18, 0.82)',
      }}
    >
      {/* Само окно нажатия не пропускает — иначе закрывалось бы от касания по
          собственному тексту. */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 340,
          textAlign: 'center',
          border: '1px solid var(--line-strong)',
          borderRadius: 20,
          padding: '26px 22px',
          background: 'rgba(24, 14, 38, 0.98)',
        }}
      >
        <div style={{ fontSize: 40, color: 'var(--gold-bright)', lineHeight: 1 }}>✦</div>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 38, color: 'var(--gold-bright)', marginTop: 6 }}>
          {stars > 0 ? `+${stars}` : ''}
        </div>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--ink)', marginTop: 6 }}>{title}</div>
        <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-muted)', marginTop: 8 }}>{body}</div>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 20,
            width: '100%',
            background: 'var(--gold-gradient)',
            color: '#1a1020',
            border: 'none',
            borderRadius: 14,
            padding: '13px 20px',
            fontFamily: 'var(--sans)',
            fontSize: 15.5,
            fontWeight: 600,
          }}
        >
          {ok}
        </button>
      </div>
    </div>,
    document.body,
  )
}
